import { useEffect } from 'react';
import { brandForBranch, normalizeBrand, thaiPhoneE164 } from './PublicBrand';

export const SITE_ORIGIN = String(
  process.env.REACT_APP_PUBLIC_SITE_URL || 'https://saanti-niranaspa.com'
).replace(/\/+$/, '');

export const SITE_NAME = 'SAANTI Thai Massage';
export const DEFAULT_IMAGE = `${SITE_ORIGIN}/spa-images/saanti-009.webp`;

const SOCIAL_PROFILES = [
  'https://www.instagram.com/saanti.thaimassage/',
  'https://www.facebook.com/people/Saanti-Thai-Massage-Songwat/61574191362794/',
  'https://www.tiktok.com/@saantithaimassagesongwat',
];

const absoluteUrl = (path = '/') => `${SITE_ORIGIN}${path === '/' ? '' : path}`;

/**
 * Route-aware search and social metadata for the public React application.
 * Every node is tagged and removed during route transitions so an old page's
 * canonical, robots rule, or JSON-LD can never leak into the next page.
 */
export default function Seo({
  title,
  description,
  canonicalPath = '/',
  image = DEFAULT_IMAGE,
  imageAlt = 'SAANTI Thai Massage on Song Wat Road in Bangkok',
  type = 'website',
  noindex = false,
  breadcrumbs = [],
  structuredData = [],
  lang = 'en',
}) {
  const canonical = absoluteUrl(canonicalPath);
  const graph = [
    {
      '@type': 'ImageObject',
      '@id': `${image}#primaryimage`,
      url: image,
      contentUrl: image,
      caption: imageAlt,
    },
    {
      '@type': 'WebPage',
      '@id': `${canonical}#webpage`,
      url: canonical,
      name: title,
      description,
      isPartOf: { '@id': `${SITE_ORIGIN}/#website` },
      inLanguage: lang,
      primaryImageOfPage: { '@id': `${image}#primaryimage` },
    },
    ...(breadcrumbs.length > 1 ? [{
      '@type': 'BreadcrumbList',
      '@id': `${canonical}#breadcrumb`,
      itemListElement: breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: absoluteUrl(item.path),
      })),
    }] : []),
    ...structuredData,
  ];
  const schemaJson = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = title;

    const created = [];
    const addMeta = (attribute, key, content) => {
      if (!content) return;
      const node = document.createElement('meta');
      node.setAttribute(attribute, key);
      node.setAttribute('content', content);
      node.dataset.saantiSeo = 'true';
      document.head.appendChild(node);
      created.push(node);
    };
    const addLink = (rel, href) => {
      const node = document.createElement('link');
      node.rel = rel;
      node.href = href;
      node.dataset.saantiSeo = 'true';
      document.head.appendChild(node);
      created.push(node);
    };

    // Remove the static fallbacks from index.html after React has mounted.
    document.head.querySelectorAll('[data-seo-fallback="true"]').forEach((node) => node.remove());

    addMeta('name', 'description', description);
    addMeta(
      'name',
      'robots',
      noindex
        ? 'noindex, nofollow, noarchive'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
    );
    addMeta('property', 'og:site_name', SITE_NAME);
    addMeta('property', 'og:type', type);
    addMeta('property', 'og:locale', 'en_TH');
    addMeta('property', 'og:title', title);
    addMeta('property', 'og:description', description);
    addMeta('property', 'og:url', canonical);
    addMeta('property', 'og:image', image);
    addMeta('property', 'og:image:alt', imageAlt);
    addMeta('name', 'twitter:card', 'summary_large_image');
    addMeta('name', 'twitter:title', title);
    addMeta('name', 'twitter:description', description);
    addMeta('name', 'twitter:image', image);
    addMeta('name', 'twitter:image:alt', imageAlt);
    addLink('canonical', canonical);

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = schemaJson;
    script.dataset.saantiSeo = 'true';
    document.head.appendChild(script);
    created.push(script);

    return () => created.forEach((node) => node.remove());
  }, [canonical, description, image, imageAlt, lang, noindex, schemaJson, title, type]);

  return null;
}

export const websiteSchema = () => ({
  '@type': 'WebSite',
  '@id': `${SITE_ORIGIN}/#website`,
  url: `${SITE_ORIGIN}/`,
  name: SITE_NAME,
  alternateName: ['SAANTI Massage', 'ศานติ นวดไทย'],
  inLanguage: ['en', 'th'],
  publisher: { '@id': `${SITE_ORIGIN}/#organization` },
});

export const organizationSchema = () => ({
  '@type': 'Organization',
  '@id': `${SITE_ORIGIN}/#organization`,
  name: SITE_NAME,
  alternateName: 'ศานติ นวดไทย',
  url: `${SITE_ORIGIN}/`,
  logo: {
    '@type': 'ImageObject',
    '@id': `${SITE_ORIGIN}/#logo`,
    url: `${SITE_ORIGIN}/spa-images/saanti-official-logo.png`,
    width: 198,
    height: 225,
  },
  email: 'sales.saanti@gmail.com',
  telephone: '+66-95-592-3236',
  sameAs: SOCIAL_PROFILES,
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+66-95-592-3236',
    contactType: 'reservations',
    availableLanguage: ['English', 'Thai'],
    areaServed: 'TH',
  },
});

const songWatAddress = {
  streetAddress: '981 Song Wat Road',
  addressLocality: 'Samphanthawong',
  addressRegion: 'Bangkok',
  postalCode: '10100',
  addressCountry: 'TH',
};

const branchAddress = (branch) => {
  if (branch?.address) {
    return {
      streetAddress: branch.address,
      addressLocality: 'Bangkok',
      addressRegion: 'Bangkok',
      addressCountry: 'TH',
    };
  }
  return branch?.code === 'SONGWAT' || !branch ? songWatAddress : null;
};

export const localBusinessSchemas = (branches) => {
  const locations = branches?.length ? branches : [{
    code: 'SONGWAT',
    name: 'Song Wat',
    nameTh: 'สาขาทรงวาด',
    phone: '+66 95 592 3236',
    hours: '10:00 - 22:00',
  }];

  return locations.flatMap((branch) => {
    const address = branchAddress(branch);
    if (!address) return [];
    const code = String(branch.code || 'SONGWAT').toLowerCase();
    const [opens = '10:00', closes = '22:00'] = String(branch.hours || '').split(/\s*-\s*/);
    const name = `${SITE_NAME} ${branch.name || 'Song Wat'}`;
    const brand = brandForBranch(branch) || 'saanti';
    const bookingUrl = `${SITE_ORIGIN}/book?brand=${encodeURIComponent(brand)}`;

    return [{
      '@type': 'DaySpa',
      '@id': `${SITE_ORIGIN}/#location-${code}`,
      name,
      alternateName: branch.nameTh || 'ศานติ นวดไทย สาขาทรงวาด',
      description: 'Traditional Thai massage, aroma oil massage, foot massage and restorative wellness treatments in Bangkok.',
      url: `${SITE_ORIGIN}/contact?brand=${encodeURIComponent(brand)}`,
      image: [
        `${SITE_ORIGIN}/spa-images/saanti-009.webp`,
        `${SITE_ORIGIN}/spa-images/saanti-070.webp`,
        `${SITE_ORIGIN}/spa-images/saanti-106.webp`,
      ],
      logo: { '@id': `${SITE_ORIGIN}/#logo` },
      telephone: thaiPhoneE164(branch.phone) || '+66955923236',
      email: 'sales.saanti@gmail.com',
      priceRange: '฿฿',
      currenciesAccepted: 'THB',
      paymentAccepted: 'Cash, Credit Card, PromptPay',
      address: { '@type': 'PostalAddress', ...address },
      openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [
          'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
        ],
        opens: opens || '10:00',
        closes: closes === '24:00' ? '23:59' : (closes || '22:00'),
      },
      sameAs: SOCIAL_PROFILES,
      potentialAction: {
        '@type': 'ReserveAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: bookingUrl,
          actionPlatform: [
            'https://schema.org/DesktopWebPlatform',
            'https://schema.org/MobileWebPlatform',
          ],
        },
        result: { '@type': 'Reservation', name: 'Massage reservation' },
      },
    }];
  });
};

export const serviceListSchema = (services, branchCode = 'SONGWAT', brand = 'saanti') => ({
  '@type': 'ItemList',
  '@id': `${SITE_ORIGIN}/services#treatments`,
  name: 'SAANTI massage treatments and prices',
  itemListElement: (services || []).map((service, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'Service',
      name: service.name_en,
      alternateName: service.name_th || undefined,
      description: service.description || undefined,
      image: service.image_url || undefined,
      provider: { '@id': `${SITE_ORIGIN}/#location-${String(branchCode).toLowerCase()}` },
      areaServed: { '@type': 'City', name: 'Bangkok' },
      offers: (service.variants || []).map((variant) => ({
        '@type': 'Offer',
        price: Number(variant.price),
        priceCurrency: 'THB',
        availability: 'https://schema.org/InStock',
        url: `${SITE_ORIGIN}/book?brand=${encodeURIComponent(normalizeBrand(brand))}`,
        description: `${variant.durationMinutes} minute treatment`,
      })),
    },
  })),
});
